// src/pages/Compose.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, X, Paperclip, Save } from 'lucide-react';
import { emailService } from '../services/email';

export default function Compose() {
    const navigate = useNavigate();
    const location = useLocation();
    const draft = location.state?.draft;

    const [formData, setFormData] = useState({
        to: draft?.to || '',
        cc: draft?.cc || '',
        bcc: draft?.bcc || '',
        subject: draft?.subject || '',
        body: draft?.body || ''
    });
    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [lastSaved, setLastSaved] = useState(null);
    const [draftId, setDraftId] = useState(draft?.id || null);

    // Auto-save draft every 30 seconds
    useEffect(() => {
        const autoSaveInterval = setInterval(() => {
            if (formData.to || formData.subject || formData.body) {
                handleSaveDraft(true); // Silent save
            }
        }, 30000);

        return () => clearInterval(autoSaveInterval);
    }, [formData]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSaveDraft = async (silent = false) => {
        if (!silent) setSaving(true);

        try {
            const savedDraft = emailService.saveDraft({
                ...formData,
                id: draftId
            });

            if (savedDraft) {
                setDraftId(savedDraft.id);
                setLastSaved(new Date());
                if (!silent) {
                    setError('');
                    setTimeout(() => setSaving(false), 1000);
                }
            }
        } catch (err) {
            if (!silent) {
                setError('Failed to save draft');
                setSaving(false);
            }
        }
    };

    const handleSend = async () => {
        if (!formData.to) {
            setError('Please enter a recipient');
            return;
        }

        setSending(true);
        setError('');

        try {
            await emailService.sendEmail(formData);

            // Delete draft if it exists
            if (draftId) {
                emailService.deleteDraft(draftId);
            }

            navigate('/sent');
        } catch (err) {
            setError('Failed to send email. Please try again.');
            console.error(err);
        } finally {
            setSending(false);
        }
    };

    const handleDiscard = () => {
        if (formData.to || formData.subject || formData.body) {
            if (window.confirm('Discard this email?')) {
                if (draftId) {
                    emailService.deleteDraft(draftId);
                }
                navigate('/inbox');
            }
        } else {
            navigate('/inbox');
        }
    };

    return (
        <div className="h-full flex flex-col bg-white">
            <div className="border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                    {draftId ? 'Edit Draft' : 'New Message'}
                </h2>
                <button
                    onClick={handleDiscard}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            {error && (
                <div className="px-6 py-2 bg-red-50 text-red-600 text-sm">
                    {error}
                </div>
            )}

            {lastSaved && (
                <div className="px-6 py-1 bg-green-50 text-green-600 text-xs">
                    Draft saved at {lastSaved.toLocaleTimeString()}
                </div>
            )}

            <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-4 space-y-4">
                    <div>
                        <input
                            type="email"
                            name="to"
                            placeholder="To"
                            value={formData.to}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border-b border-gray-200 focus:border-blue-500 focus:outline-none"
                            multiple
                        />
                    </div>

                    <div>
                        <input
                            type="email"
                            name="cc"
                            placeholder="Cc"
                            value={formData.cc}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border-b border-gray-200 focus:border-blue-500 focus:outline-none"
                            multiple
                        />
                    </div>

                    <div>
                        <input
                            type="email"
                            name="bcc"
                            placeholder="Bcc"
                            value={formData.bcc}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border-b border-gray-200 focus:border-blue-500 focus:outline-none"
                            multiple
                        />
                    </div>

                    <div>
                        <input
                            type="text"
                            name="subject"
                            placeholder="Subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border-b border-gray-200 focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <textarea
                            name="body"
                            placeholder="Compose email"
                            value={formData.body}
                            onChange={handleChange}
                            rows={15}
                            className="w-full px-3 py-2 resize-none focus:outline-none"
                        />
                    </div>
                </div>
            </div>

            <div className="border-t px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button className="flex items-center text-gray-600 hover:text-gray-800">
                        <Paperclip className="h-5 w-5 mr-2" />
                        Attach
                    </button>
                </div>

                <div className="flex items-center space-x-4">
                    <button
                        onClick={handleDiscard}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        Discard
                    </button>
                    <button
                        onClick={() => handleSaveDraft(false)}
                        disabled={saving}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        <Send className="h-4 w-4 mr-2" />
                        {sending ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
        </div>
    );
}